import axios from "axios";
import * as v from "valibot";

import { DEFAULT_NEXUS_DATA } from "src/consts/nexus";
import { KEY } from "src/consts/nexus-keys";
import { NexusData, nexusSchema } from "src/types/nexus";

const API_KEY = process.env.BIG_FLIP_NEXUS_API_KEY;
const NEXUS_URL = process.env.BIG_FLIP_NEXUS_BASE_API;

// Isolated axios instance for the Nexus feature-flag service. It deliberately does
// NOT reuse the shared RequestManager: Nexus authenticates via X-Api-Key (not the
// user's Bearer token) and a Nexus failure must never trigger the app's
// logout/maintenance interceptors — it should silently fall back to defaults.
const nexusClient = axios.create({
  baseURL: NEXUS_URL,
  headers: {
    "X-Api-Key": API_KEY
  }
});

const GENERAL_FLAGS = Object.values(KEY);

// Core fetch+parse. Returns null on network error or schema-parse failure so each
// caller can decide its own fallback: initial load falls back to defaults, while a
// mid-session refetch keeps the current (good) state instead of clobbering it.
const fetchNexusVariants = async (merchantId: string): Promise<NexusData | null> => {
  const { data } = await nexusClient.get("/v1/variants", {
    params: {
      device_id: merchantId,
      flag: GENERAL_FLAGS.join(",")
    }
  });

  const result = v.safeParse(nexusSchema, data);
  if (!result.success) {
    console.error("Invalid Nexus payload", v.flatten(result.issues));
    return null;
  }

  return result.output;
};

// Initial load. Falling back to defaults on any failure is correct here: there is no
// prior good state to protect, and defaults keep every flag "off" (legacy).
export const initNexus = async (merchantId = "ffb"): Promise<NexusData> => {
  try {
    return (await fetchNexusVariants(merchantId)) ?? DEFAULT_NEXUS_DATA;
  } catch (error) {
    console.error(error);
    return DEFAULT_NEXUS_DATA;
  }
};

// Mid-session refetch (rollout/rollback). Returns null on failure ON PURPOSE so the
// caller KEEPS the current state — a transient blip must never silently flip a
// rolled-out merchant back to all-legacy defaults. See refetch-controller.ts.
export const refetchNexus = async (merchantId = "ffb"): Promise<NexusData | null> => {
  try {
    return await fetchNexusVariants(merchantId);
  } catch (error) {
    console.error(error);
    return null;
  }
};
